import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { Settings, MapPin, Bell, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountSettingsPage() {
  const { isBarOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    business_name: '',
    location: { city: '', state: '' },
    notification_preferences: {
      new_applications: true,
      new_messages: true,
    },
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/owner/settings');
      const data = response.data || {};
      setFormData({
        email: data.email || '',
        business_name: data.business_name || '',
        location: {
          city: data.location?.city || '',
          state: data.location?.state || '',
        },
        notification_preferences: {
          new_applications: Boolean(data.notification_preferences?.new_applications),
          new_messages: Boolean(data.notification_preferences?.new_messages),
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load account settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isBarOwner) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [isBarOwner]);

  const saveSettings = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      await axios.put('/owner/settings', {
        business_name: formData.business_name,
        location: formData.location,
        notification_preferences: formData.notification_preferences,
      });
      toast.success('Account settings saved');
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account settings');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New password and confirm password must match');
      return;
    }
    try {
      setChangingPassword(true);
      await axios.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password changed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!isBarOwner) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="account-settings-restricted-state">
        <Card className="bg-white/5 backdrop-blur-md border border-white/10">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-semibold text-white mb-2">Owner Access Only</h2>
            <p className="text-muted-foreground">Account Settings are available for bar owner accounts.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="account-settings-loading-state">
        <div className="h-40 rounded-xl border border-white/10 bg-white/5 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="account-settings-page">
      <Card className="bg-white/5 backdrop-blur-md border border-white/10 mb-6">
        <CardHeader>
          <CardTitle className="text-3xl font-['Barlow_Condensed'] uppercase text-white flex items-center gap-2">
            <Settings className="h-6 w-6 text-primary" />
            Account Settings
          </CardTitle>
          <CardDescription>Manage your bar account details and communication preferences.</CardDescription>
        </CardHeader>
      </Card>

      <form onSubmit={saveSettings} className="space-y-6" data-testid="account-settings-form">
        <Card className="bg-white/5 backdrop-blur-md border border-white/10">
          <CardHeader>
            <CardTitle>Profile & Location</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="settings-email">Email</Label>
              <Input id="settings-email" value={formData.email} readOnly data-testid="settings-email-input" />
            </div>
            <div>
              <Label htmlFor="settings-business-name">Business / Bar Name</Label>
              <Input
                id="settings-business-name"
                value={formData.business_name}
                onChange={(event) => setFormData((prev) => ({ ...prev, business_name: event.target.value }))}
                data-testid="settings-business-name-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="settings-city">City</Label>
                <Input
                  id="settings-city"
                  value={formData.location.city}
                  onChange={(event) => setFormData((prev) => ({ ...prev, location: { ...prev.location, city: event.target.value } }))}
                  data-testid="settings-city-input"
                />
              </div>
              <div>
                <Label htmlFor="settings-state">State</Label>
                <Input
                  id="settings-state"
                  value={formData.location.state}
                  onChange={(event) => setFormData((prev) => ({ ...prev, location: { ...prev.location, state: event.target.value } }))}
                  data-testid="settings-state-input"
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground inline-flex items-center" data-testid="settings-location-hint">
              <MapPin className="h-3 w-3 mr-1" />
              Keep this accurate for better local bartender matching.
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-md border border-white/10">
          <CardHeader>
            <CardTitle className="inline-flex items-center"><Bell className="h-4 w-4 mr-2" />Notification Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between" data-testid="settings-new-applications-toggle-row">
              <div>
                <p className="text-white text-sm">New Applications</p>
                <p className="text-xs text-muted-foreground">Notify when bartenders apply to your jobs.</p>
              </div>
              <Switch
                checked={formData.notification_preferences.new_applications}
                onCheckedChange={(checked) => setFormData((prev) => ({
                  ...prev,
                  notification_preferences: { ...prev.notification_preferences, new_applications: checked },
                }))}
                data-testid="settings-new-applications-switch"
              />
            </div>
            <div className="flex items-center justify-between" data-testid="settings-new-messages-toggle-row">
              <div>
                <p className="text-white text-sm">New Messages</p>
                <p className="text-xs text-muted-foreground">Notify when bartenders send you messages.</p>
              </div>
              <Switch
                checked={formData.notification_preferences.new_messages}
                onCheckedChange={(checked) => setFormData((prev) => ({
                  ...prev,
                  notification_preferences: { ...prev.notification_preferences, new_messages: checked },
                }))}
                data-testid="settings-new-messages-switch"
              />
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={saving} data-testid="settings-save-btn">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>

      <Card className="bg-white/5 backdrop-blur-md border border-white/10 mt-6">
        <CardHeader>
          <CardTitle className="inline-flex items-center"><Lock className="h-4 w-4 mr-2" />Change Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label htmlFor="settings-current-password">Current Password</Label>
              <Input
                id="settings-current-password"
                type="password"
                value={passwordForm.current_password}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, current_password: event.target.value }))}
                data-testid="settings-current-password-input"
              />
            </div>
            <div>
              <Label htmlFor="settings-new-password">New Password</Label>
              <Input
                id="settings-new-password"
                type="password"
                value={passwordForm.new_password}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))}
                data-testid="settings-new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="settings-confirm-password">Confirm New Password</Label>
              <Input
                id="settings-confirm-password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirm_password: event.target.value }))}
                data-testid="settings-confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={changingPassword} variant="outline" data-testid="change-password-submit-btn">
              {changingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
